import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../components/primitives/data-display/DataSequence";
import { EmptyState } from "../../../components/primitives/feedback/EmptyState";
import { LoadingState } from "../../../components/primitives/feedback/LoadingState";
import { gaps } from "../../../components/primitives/spacing";
import type { BacklogFieldCount } from "../../../lib/corpusApi";
import { fieldLabel } from "../../../lib/scorecardFieldUtils";
import type { UseAnnotationLandingResult } from "./useAnnotationLanding";
import { ROUTES } from "../../../route_paths";

interface BacklogPanelProps {
  landing: UseAnnotationLandingResult;
}

/** The blocking Tier-1 field breakdown. "Start annotating" (or a row's "Review") enters the
 *  backlog stream, which serves one (document, field) work item at a time. */
export function BacklogPanel({ landing }: BacklogPanelProps) {
  const navigate = useNavigate();
  const { fields, total, loading, error } = landing;

  function startAnnotating(field?: string) {
    navigate(
      field
        ? `${ROUTES.internal.annotation.review.path()}?field=${field}`
        : ROUTES.internal.annotation.review.path()
    );
  }

  const columns: DataSequenceColumn<BacklogFieldCount>[] = [
    {
      key: "field",
      header: "Field",
      cardRole: "primary",
      cell: (f) => fieldLabel(f.field),
    },
    {
      key: "count",
      header: "Documents",
      cell: (f) => `${f.count} document${f.count === 1 ? "" : "s"}`,
    },
    {
      key: "action",
      header: "",
      cardRole: "action",
      cell: (f) => (
        <Chip label="Review" size="small" clickable onClick={() => startAnnotating(f.field)} />
      ),
      align: "right",
    },
  ];

  if (loading) return <LoadingState label="Loading backlog…" />;
  if (error) return <EmptyState title="Couldn't load the backlog" message={error} />;
  if (total === 0) {
    return (
      <EmptyState
        title="Backlog is clear"
        message="No documents are currently blocked on a Tier-1 field."
      />
    );
  }

  return (
    <Stack gap={gaps.base}>
      <Button
        variant="contained"
        onClick={() => startAnnotating()}
        sx={{ alignSelf: "flex-start" }}
      >
        Start annotating
      </Button>
      <DataSequence columns={columns} rows={fields} getRowId={(f) => f.field} />
    </Stack>
  );
}
